import * as types from '@/store/types'
import { throwError } from '@/utils/store'
import { toSnakeCaseString } from '@/utils/common'
import { createWhitelistNotification, createBlacklistNotification } from '@/utils/notification'

export default {
  [types.FETCH_ALL_PLACEMENTS]: ({ commit, getters }, { limit = 20 } = {}) => {
    const placementsTypesMap = {
      [types.WORST_PLACEMENTS]: 'worst',
      [types.DISPUTED_PLACEMENTS]: 'disputed',
      [types.BEST_PLACEMENTS]: 'best'
    }
    const placementsTypes = Object.keys(placementsTypesMap)

    placementsTypes.forEach(name => commit(types.LOADING, name))
    return getters.apiService
      .fetchAllPlacements({ limit })
      .then(({
        isValidConversions,
        hasActiveCampaigns,
        conversionsSyncDate,
        placements
      }) => {
        commit(types.SET_CONVERSIONS_SYNC_DATE, { conversionsSyncDate })

        Object.entries(placementsTypesMap).forEach(([name, responseKey]) => {
          const { results: list, count } = placements[responseKey]
          const totalPage = Math.ceil(count / limit)
          commit(types.SET_LIST, { name, list })
          commit(types.UPDATE_PARAMS, {
            name,
            params: {
              totalPage,
              isValidConversions,
              hasActiveCampaigns,
              count
            }
          })
        })
      })
      .catch(throwError(commit, 'Ошибка получения списка площадок'))
      .finally(() => placementsTypes.forEach(name => commit(types.LOADED, name)))
  },

  [types.FETCH_PLACEMENTS_BY_TYPE]: (
    { commit, getters, state },
    {
      name
    }
  ) => {
    const stateModule = state[name]
    const { dateRange, limit } = stateModule.params
    const value = dateRange && dateRange.value ? dateRange.value : null
    const during = ['ALL_TIME', 'OTHER_RANGE'].includes(value)
      ? null
      : value
    const [startDate, endDate] = stateModule.params.customDateRange
    const page = stateModule.params.currentPage
    const { prop, order } = stateModule.params.sort
    const ordering = order === 'descending' ? `-${toSnakeCaseString(prop)}` : toSnakeCaseString(prop)

    const options = {
      date_range_type: during,
      date_from: startDate,
      date_to: endDate,
      limit,
      page,
      ordering
    }

    commit(types.LOADING, name)
    return getters.apiService
      .fetchPlacementsByType(name, options)
      .then(({
        isValidConversions, list, count, totalPage
      }) => {
        commit(types.SET_LIST, { name, list })
        commit(types.UPDATE_PARAMS, { name, params: { totalPage, isValidConversions, count } })
      })
      .catch(throwError(commit, `Ошибка получения списка площадок (${name})`))
      .finally(() => commit(types.LOADED, name))
  },

  [types.FETCH_BLACKLIST_PLACEMENTS]: (
    { commit, getters, state }
  ) => {
    const name = types.BLACKLIST_PLACEMENTS
    const stateModule = state[name]
    const page = stateModule.params.currentPage
    const { limit } = stateModule.params
    const { value: campaign, type: level } = stateModule.params.level
    const isCampaignLevel = stateModule.params.level.type === 'campaign'
    const { prop, order } = stateModule.params.sort
    const ordering = order === 'descending' ? `-${toSnakeCaseString(prop)}` : toSnakeCaseString(prop)
    const options = {
      level,
      page,
      limit,
      ordering,
      campaign_id: isCampaignLevel ? campaign : null
    }

    commit(types.LOADING, name)
    return getters.apiService
      .fetchBlacklistPlacements(options)
      .then(({ list, totalPage }) => {
        commit(types.SET_LIST, { name, list })
        commit(types.UPDATE_PARAMS, { name, params: { totalPage } })
      })
      .catch(throwError(commit, 'Ошибка получения черного списка площадок'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.FETCH_WHITELIST_PLACEMENTS]: (
    { commit, getters, state }
  ) => {
    const name = types.WHITELIST_PLACEMENTS
    const stateModule = state[name]
    const page = stateModule.params.currentPage
    const { limit } = stateModule.params
    const { prop, order } = stateModule.params.sort
    const ordering = order === 'descending' ? `-${toSnakeCaseString(prop)}` : toSnakeCaseString(prop)
    const options = {
      page,
      limit,
      ordering
    }

    commit(types.LOADING, name)
    return getters.apiService
      .fetchWhitelistPlacements(options)
      .then(({ list, totalPage }) => {
        commit(types.SET_LIST, { name, list })
        commit(types.UPDATE_PARAMS, { name, params: { totalPage } })
      })
      .catch(throwError(commit, 'Ошибка получения белого списка площадок'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.CHANGE_BLACKLIST_PLACEMENTS]: ({ commit, getters, dispatch }, { name, ids, action }) => {
    commit(types.LOADING, name)
    return getters.apiService
      .changeBlacklistPlacements({ ids, action })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createBlacklistNotification(result)))
      .catch(throwError(commit, `Ошибка изменения площадок черного списка (from ${name})`))
      .finally(() => commit(types.LOADED, name))
  },

  [types.CHANGE_WHITELIST_PLACEMENTS]: ({ commit, getters, dispatch }, { name, ids, action }) => {
    commit(types.LOADING, name)
    return getters.apiService
      .changeWhitelistPlacements({ ids, action })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createWhitelistNotification(result)))
      .catch(throwError(commit, `Ошибка изменения площадок белого списка (from ${name})`))
      .finally(() => commit(types.LOADED, name))
  },

  [types.MOVE_BLACKLIST_PLACEMENTS_TO_WHITELIST]: ({ commit, getters }, { name, ids }) => {
    commit(types.LOADING, name)
    return getters.apiService
      .moveBlacklistPlacementsToWhitelist({ ids })
      .catch(throwError(commit, 'Ошибка перемещения площадок из черного в белый список'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.ADD_PLACEMENTS_TO_WHITELIST_FROM_FILE]: ({ commit, getters, dispatch }, { formData }) => {
    const name = types.WHITELIST_PLACEMENTS
    commit(types.LOADING, name)
    return getters.apiService
      .addPlacementsToWhitelistFromFile({ formData })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createWhitelistNotification(result)))
      .catch(throwError(commit, 'Ошибка добавления площадок в Белый список из файла'))
      .finally(() => commit(types.LOADED, name))
  },
  [types.ADD_PLACEMENTS_TO_BLACKLIST_FROM_FILE]: (
    {
      state, commit, getters, dispatch
    },
    { formData }
  ) => {
    const name = types.BLACKLIST_PLACEMENTS
    const { campaign } = state[name].params
    commit(types.LOADING, name)
    return getters.apiService
      .addPlacementsToBlacklistFromFile({ campaign, formData })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createBlacklistNotification(result)))
      .catch(throwError(commit, 'Ошибка добавления площадок в Черный список из файла'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.ADD_PLACEMENTS_TO_WHITELIST_MANUALLY]: ({ commit, getters, dispatch }, { domains }) => {
    const name = types.WHITELIST_PLACEMENTS
    return getters.apiService
      .addPlacementsToWhitelistManually({ domains })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createWhitelistNotification(result)))
      .catch(throwError(commit, 'Ошибка ручного добавления площадок в Белый список'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.ADD_PLACEMENTS_TO_BLACKLIST_MANUALLY]: (
    {
      state, commit, getters, dispatch
    },
    { domains }
  ) => {
    const name = types.BLACKLIST_PLACEMENTS
    const { level } = state[name].params
    commit(types.LOADING, name)
    return getters.apiService
      .addPlacementsToBlacklistManually({ campaign: level.value, domains })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createBlacklistNotification(result)))
      .catch(throwError(commit, 'Ошибка ручного добавления площадок в Черный список'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.ADD_ALL_PLACEMENTS_TO_WHITELIST]: ({ commit, getters }, { name, during }) => {
    const availableTypes = {
      [types.WORST_PLACEMENTS]: 'worst',
      [types.DISPUTED_PLACEMENTS]: 'disputable',
      [types.BEST_PLACEMENTS]: 'best'
    }
    const type = availableTypes[name]

    commit(types.LOADING, name)
    return getters.apiService
      .addAllPlacementsToWhitelist({ type, during })
      .catch(throwError(commit, 'Ошибка добавления всех площадок в Белый список'))
      .finally(() => commit(types.LOADED, name))
  },

  [types.ADD_ALL_PLACEMENTS_TO_BLACKLIST]: ({ commit, getters, dispatch }, { name, during }) => {
    const availableTypes = {
      [types.WORST_PLACEMENTS]: 'worst',
      [types.DISPUTED_PLACEMENTS]: 'disputable',
      [types.BEST_PLACEMENTS]: 'best'
    }
    const type = availableTypes[name]

    commit(types.LOADING, name)
    return getters.apiService
      .addAllPlacementsToBlacklist({ type, during })
      .then(result => dispatch(types.PUSH_NOTIFICATION, createBlacklistNotification(result)))
      .catch(throwError(commit, 'Ошибка добавления всех площадок в Черный список'))
      .finally(() => commit(types.LOADED, name))
  }
}
